import React, { useState, useEffect, useRef } from 'react';
import { useJamWebSocket } from '../contexts/JamWebSocketContext';
import { useSpotifyPlayer } from '../contexts/SpotifyPlayerContext';
import axios from 'axios';
import AddTrackModal from './AddTrackModal';
import DiscoverNewAlert from './DiscoverNewAlert';
import TrackSelectionModal from './TrackSelectionModal';
import InviteFriendsModal from './InviteFriendsModal';
import SpotifyLibraryModal from './SpotifyLibraryModal';
import EmotePicker from './EmotePicker';
import EmoteInput from './EmoteInput';
import { renderEmotesInText } from '../utils/emoteRenderer';
import '../App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JamSessionModal = ({ session, userToken, userId, username, onClose, onNavigateToDiscover, setIsDiscoveringForJam, setTargetSessionId, onSelectLocalTrack }) => {
  const { connect, disconnect, send, addListener, isConnected } = useJamWebSocket();
  const spotifyPlayer = useSpotifyPlayer();
  const audioRef = useRef(null);
  
  // Spotify state
  const [spotifyToken, setSpotifyToken] = useState(null);
  const [useSpotifyPlayback, setUseSpotifyPlayback] = useState(false);
  const [showPremiumAlert, setShowPremiumAlert] = useState(false);
  const spotifyInitializedRef = useRef(false); // Track if Spotify has been initialized
  const waitForReadyIntervalRef = useRef(null); // Track the wait interval
  
  // Queue dropdown state
  const [showQueueDropdown, setShowQueueDropdown] = useState(false);
  
  // Session state
  const [currentTrack, setCurrentTrack] = useState(session?.current_track || null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [participants, setParticipants] = useState(session?.participants || []);
  const [queue, setQueue] = useState(session?.queue || []);
  const [chatMessages, setChatMessages] = useState(session?.chat_messages || []);

  // Update session state when session prop changes
  useEffect(() => {
    if (session) {
      console.log('📝 Session prop updated:');
      console.log('   Current track:', session.current_track);
      if (session.current_track) {
        console.log('   Title:', session.current_track.title);
        console.log('   Artist:', session.current_track.artist);
        console.log('   Preview URL:', session.current_track.preview_url);
        console.log('   Spotify URI:', session.current_track.spotify_uri);
        console.log('   Duration:', session.current_track.duration_ms);
        
        if (!session.current_track.preview_url && !session.current_track.spotify_uri) {
          console.warn('⚠️ Track has neither preview_url nor spotify_uri - cannot play!');
        }
      }
      setCurrentTrack(session.current_track);
      setQueue(session.queue || []);
      setParticipants(session.participants || []);
    }
  }, [session]);
  
  // UI state
  const [chatInput, setChatInput] = useState('');
  const [showReactions, setShowReactions] = useState(false);
  const [showAddTrackModal, setShowAddTrackModal] = useState(false);
  const [showDiscoverAlert, setShowDiscoverAlert] = useState(false);
  const [showTrackSelection, setShowTrackSelection] = useState(false);
  const [trackSelectionSource, setTrackSelectionSource] = useState(null); // 'favorites' or 'saved'
  const [showSpotifyLibrary, setShowSpotifyLibrary] = useState(false);
  const [showInviteFriends, setShowInviteFriends] = useState(false);
  const [showChatPanel, setShowChatPanel] = useState(false); // NEW: sliding chat panel
  const [showEmotePicker, setShowEmotePicker] = useState(false); // NEW: emote picker
  const [globalEmotes, setGlobalEmotes] = useState([]);
  const [customEmotes, setCustomEmotes] = useState([]);
  const chatEndRef = useRef(null);
  
  const isHost = session?.host_user_id === userId;

  // Fetch emotes on mount + refetch when a new custom emote is added elsewhere
  useEffect(() => {
    const fetchEmotes = async () => {
      try {
        const [globalRes, customRes] = await Promise.all([
          axios.get(`${API}/emotes/global`),
          axios.get(`${API}/emotes/my-custom`, {
            headers: { 'Authorization': `Bearer ${userToken}` }
          })
        ]);
        setGlobalEmotes(globalRes.data || []);
        setCustomEmotes(customRes.data || []);
      } catch (err) {
        console.error('Failed to fetch emotes:', err);
      }
    };

    fetchEmotes();
    const onChange = () => fetchEmotes();
    window.addEventListener('beatseek:custom-emotes-changed', onChange);
    return () => window.removeEventListener('beatseek:custom-emotes-changed', onChange);
  }, [userToken]);

  // Connect to WebSocket when component mounts
  useEffect(() => {
    if (session?.session_id) {
      console.log('🔌 Connecting to WebSocket for session:', session.session_id);
      connect(session.session_id);
      
      // Notify others that we joined
      setTimeout(() => {
        console.log('📢 Sending participant_joined event');
        send({
          type: 'participant_joined',
          username: username
        });
      }, 500);
    }

    return () => {
      console.log('🔌 Disconnecting WebSocket');
      disconnect();
    };
  }, [session?.session_id]);

  // Mobile rotation suggestion - show once per session
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isMobile = window.innerWidth <= 768;
    const isPortrait = window.innerHeight > window.innerWidth;
    const alreadyShown = sessionStorage.getItem('jam_rotation_alert_shown');
    if (isMobile && isPortrait && !alreadyShown) {
      const t = setTimeout(() => {
        alert('📱 Rotate your phone for the best Jam Session experience!');
        sessionStorage.setItem('jam_rotation_alert_shown', 'true');
      }, 400);
      return () => clearTimeout(t);
    }
  }, []);

  // Initialize Spotify Player when modal opens - ONLY ONCE
  useEffect(() => {
    let isMounted = true;
    let checkInterval = null;
    let waitForReadyInterval = null;
    
    const initSpotify = async () => {
      if (!isMounted || spotifyInitializedRef.current) {
        console.log('⚠️ Skipping Spotify init - already initialized or unmounted');
        return;
      }
      
      // Debug localStorage
      console.log('🔍 Checking localStorage for Spotify token...');
      console.log('   All localStorage keys:', Object.keys(localStorage));
      const token = localStorage.getItem('spotify_access_token');
      console.log('   spotify_access_token:', token ? token.substring(0, 20) + '...' : 'NULL');
      
      if (token) {
        console.log('🎵 Found Spotify token, initializing player...');
        console.log('   Token:', token.substring(0, 20) + '...');
        spotifyInitializedRef.current = true; // Mark as initialized
        setSpotifyToken(token);
        
        // Check premium status and get the RESULT (not state)
        const premiumCheckResult = await spotifyPlayer.checkPremiumStatus(token);
        console.log('   Premium check result:', premiumCheckResult);
        
        if (!premiumCheckResult) {
          console.log('⚠️ Spotify Free user - will use preview URLs');
          setShowPremiumAlert(true);
          setUseSpotifyPlayback(false);
          return;
        }
        
        // User has Premium, connect player
        const success = await spotifyPlayer.connect(token);
        console.log('   Connect result:', success);
        
        if (success && isMounted) {
          console.log('✅ Spotify connected with Premium, waiting for player to be ready...');
          
          // Wait up to 10 seconds for device ID
          let attempts = 0;
          const maxAttempts = 50; // 10 seconds (50 * 200ms)
          
          waitForReadyIntervalRef.current = setInterval(() => {
            if (!isMounted) {
              clearInterval(waitForReadyIntervalRef.current);
              waitForReadyIntervalRef.current = null;
              return;
            }
            
            attempts++;
            console.log(`⏳ Waiting for Spotify Player... (${attempts}/${maxAttempts})`);
            console.log('   isReady:', spotifyPlayer.isReady);
            console.log('   deviceId:', spotifyPlayer.deviceId);
            
            if (spotifyPlayer.isReady && spotifyPlayer.deviceId) {
              console.log('✅ Spotify Player ready for premium playback');
              console.log('   Device ID:', spotifyPlayer.deviceId);
              if (isMounted) {
                setUseSpotifyPlayback(true);
              }
              clearInterval(waitForReadyIntervalRef.current);
              waitForReadyIntervalRef.current = null;
            } else if (attempts >= maxAttempts) {
              console.warn('⚠️ Spotify Player timeout - Device ID never received');
              console.warn('   Falling back to preview URLs');
              if (isMounted) {
                setUseSpotifyPlayback(false);
              }
              clearInterval(waitForReadyIntervalRef.current);
              waitForReadyIntervalRef.current = null;
            }
          }, 200);
        } else {
          console.log('❌ Failed to connect Spotify Player');
          if (isMounted) {
            setUseSpotifyPlayback(false);
          }
        }
      } else {
        console.log('ℹ️ No Spotify token found - will use preview URLs');
        if (isMounted) {
          setUseSpotifyPlayback(false);
        }
      }
    };

    // Initialize ONCE on mount
    initSpotify();

    // Also check periodically for token (in case user connects Spotify while modal is open)
    checkInterval = setInterval(() => {
      if (!isMounted || spotifyInitializedRef.current) return;
      const token = localStorage.getItem('spotify_access_token');
      if (token && !spotifyToken) {
        console.log('🔄 Spotify token detected, initializing for first time...');
        initSpotify();
      }
    }, 2000); // Check every 2 seconds

    // Cleanup function
    return () => {
      isMounted = false;
      if (checkInterval) clearInterval(checkInterval);
      if (waitForReadyIntervalRef.current) {
        clearInterval(waitForReadyIntervalRef.current);
        waitForReadyIntervalRef.current = null;
      }
      // Don't disconnect Spotify here as it may be used elsewhere
    };
  }, []); // ✅ EMPTY DEPENDENCY ARRAY - Run only once on mount

  // Log connection status changes
  useEffect(() => {
    console.log('🔌 Connection status changed:', isConnected);
  }, [isConnected]);

  // ✅ NEW: React to Spotify Player becoming ready
  useEffect(() => {
    // Update UI state when Spotify player becomes ready
    if (spotifyPlayer.isReady && spotifyPlayer.deviceId && spotifyToken) {
      console.log('✅ Spotify Player is now ready - updating UI state');
      console.log('   Device ID:', spotifyPlayer.deviceId);
      console.log('   Setting useSpotifyPlayback to TRUE');
      
      // ✅ CRITICAL: Stop the wait interval if it's still running
      if (waitForReadyIntervalRef.current) {
        console.log('🛑 Clearing wait interval - player is ready');
        clearInterval(waitForReadyIntervalRef.current);
        waitForReadyIntervalRef.current = null;
      }
      
      setUseSpotifyPlayback(true);
    } else if (!spotifyPlayer.isReady && useSpotifyPlayback) {
      console.log('⚠️ Spotify Player disconnected - updating UI state');
      setUseSpotifyPlayback(false);
    }
  }, [spotifyPlayer.isReady, spotifyPlayer.deviceId, spotifyToken]);

  // ✅ Removed syncing - context manages isPlaying directly now
  // The modal uses spotifyPlayer.isPlaying for displaying play/pause button
  // No need to sync to local state as it causes conflicts

  // ✅ Update position from Spotify context when it changes
  useEffect(() => {
    if (useSpotifyPlayback && spotifyPlayer.position !== undefined) {
      setCurrentTime(spotifyPlayer.position / 1000); // Convert ms to seconds
    }
  }, [useSpotifyPlayback, spotifyPlayer.position]);

  // ✅ Poll Spotify position for smooth progress bar updates
  useEffect(() => {
    if (!useSpotifyPlayback || !spotifyPlayer.player || !spotifyPlayer.isPlaying) {
      return;
    }

    // Poll player state every 500ms for position updates
    const positionPoll = setInterval(async () => {
      try {
        const state = await spotifyPlayer.player.getCurrentState();
        if (state && !state.paused) {
          const positionSeconds = state.position / 1000;
          setCurrentTime(positionSeconds);
        }
      } catch (err) {
        console.error('Error polling position:', err);
      }
    }, 500);

    return () => clearInterval(positionPoll);
  }, [useSpotifyPlayback, spotifyPlayer.player, spotifyPlayer.isPlaying]);

  // Listen to WebSocket messages
  useEffect(() => {
    const removeListener = addListener((message) => {
      console.log('🎵 Jam event received:', message);

      // Only process WebSocket messages if we're the host and connected
      if (!isConnected) {
        console.log('⚠️ Ignoring WebSocket message - not connected');
        return;
      }

      switch (message.type) {
        case 'playback_state_update':
          console.log('🎵 Playback state update:', message);
          
          // ✅ FIX: Only sync state for HTML5 audio, NOT Spotify playback
          // Spotify player state is managed by SpotifyPlayerContext
          if (!useSpotifyPlayback) {
            setIsPlaying(message.is_playing);
            if (audioRef.current && message.position !== undefined) {
              audioRef.current.currentTime = message.position;
              if (message.is_playing && audioRef.current.paused) {
                audioRef.current.play().catch(e => console.error('Play error:', e));
              } else if (!message.is_playing && !audioRef.current.paused) {
                audioRef.current.pause();
              }
            }
          } else {
            console.log('⏭️ Skipping WebSocket playback sync - using Spotify player');
          }
          break;

        case 'track_changed':
          setCurrentTrack(message.track);
          // ✅ Only reset time for HTML5 audio
          if (!useSpotifyPlayback) {
            setCurrentTime(0);
            if (audioRef.current) {
              audioRef.current.currentTime = 0;
            }
          }
          break;

        case 'seek':
          // ✅ FIX: Only process seek for HTML5 audio, NOT Spotify
          // Spotify player position is managed by SpotifyPlayerContext
          if (!useSpotifyPlayback && audioRef.current && message.position !== undefined) {
            audioRef.current.currentTime = message.position;
            setCurrentTime(message.position);
          } else if (useSpotifyPlayback) {
            console.log('⏭️ Skipping WebSocket seek - using Spotify player position');
          }
          break;

        case 'chat_message':
          setChatMessages(prev => {
            // Dedupe: skip if we already have an identical optimistic entry from this user
            const isDup = prev.some(m =>
              m.user_id === message.user_id &&
              m.message === message.message &&
              Math.abs(new Date(m.timestamp).getTime() - new Date(message.timestamp).getTime()) < 5000
            );
            if (isDup) return prev;
            return [...prev, {
              user_id: message.user_id,
              username: message.username,
              message: message.message,
              timestamp: message.timestamp
            }];
          });
          break;

        case 'reaction':
          // Show reaction animation
          showReactionAnimation(message.reaction, message.username);
          break;

        case 'participant_joined':
          // Refresh participants list
          refreshSession();
          break;

        default:
          console.log('Unknown message type:', message.type);
      }
    });

    return removeListener;
  }, [addListener]);

  // Auto-scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const refreshSession = async () => {
    try {
      const response = await axios.get(`${API}/jam/${session.session_id}`);
      setParticipants(response.data.participants);
      setQueue(response.data.queue);
    } catch (err) {
      console.error('Failed to refresh session:', err);
    }
  };

  // Host controls
  const togglePlayPause = async () => {
    if (!isHost) return;
    
    // Safety check: No track to play
    if (!currentTrack) {
      console.warn('⚠️ No track selected to play');
      return;
    }
    
    // Safety check: No audio source available
    if (!currentTrack.spotify_uri && !currentTrack.preview_url) {
      console.warn('⚠️ Track has no playable audio source');
      alert('This track has no playable audio source. Try adding a track from Spotify.');
      return;
    }
    
    // Check if current track has Spotify URI and we should use Spotify playback
    const isSpotifyTrack = currentTrack?.spotify_uri && useSpotifyPlayback && spotifyPlayer.isReady;
    
    // Determine current playing state based on playback method
    const currentlyPlaying = isSpotifyTrack ? spotifyPlayer.isPlaying : isPlaying;
    const newIsPlaying = !currentlyPlaying;
    
    console.log('🎵 Toggle play/pause:', { 
      currentlyPlaying, 
      newIsPlaying, 
      useSpotifyPlayback, 
      isConnected,
      hasSpotifyUri: !!currentTrack?.spotify_uri,
      hasPreviewUrl: !!currentTrack?.preview_url,
      isSpotifyTrack
    });
    
    // For HTML5 audio, update local state
    if (!isSpotifyTrack) {
      setIsPlaying(newIsPlaying);
    }
    // For Spotify, context will handle state updates
    
    try {
      if (isSpotifyTrack) {
        // Use Spotify Web Playback SDK
        console.log('🎵 Using Spotify Web Playback SDK');
        
        if (newIsPlaying) {
          // CRITICAL: Activate player on first user interaction (play button click)
          if (spotifyPlayer.player && !spotifyPlayer.player._activated) {
            console.log('🎯 Activating Spotify Player on user click...');
            spotifyPlayer.player.activateElement();
            spotifyPlayer.player._activated = true; // Mark as activated
            console.log('✅ Spotify Player activated via user interaction');
          }
          
          if (!spotifyPlayer.isPlaying) {
            // Start playback with Spotify
            const success = await spotifyPlayer.play(
              currentTrack.spotify_uri,
              Math.floor(spotifyPlayer.position || 0)
            );
            if (success) {
              console.log('✅ Spotify playback started');
              // Don't manually set isPlaying - let context handle it via player_state_changed
            }
          }
        } else {
          const success = await spotifyPlayer.pause();
          if (success) {
            console.log('⏸ Spotify playback paused');
            // Don't manually set isPlaying - let context handle it via player_state_changed
          }
        }
      } else {
        // Use regular HTML5 audio for preview URLs
        console.log('🎵 Using HTML5 audio preview');
        const position = audioRef.current?.currentTime || 0;
        
        if (newIsPlaying) {
          await audioRef.current?.play();
          console.log('✅ Audio playing');
        } else {
          audioRef.current?.pause();
          console.log('⏸ Audio paused');
        }
        
        // Send via WebSocket if connected
        if (isConnected) {
          send({
            type: 'playback_control',
            is_playing: newIsPlaying,
            position: position
          });
        }
      }
    } catch (error) {
      console.error('❌ Playback control error:', error);
      setIsPlaying(!newIsPlaying); // Revert state on error
    }
  };

  const handleSeek = async (e) => {
    if (!isHost) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    
    if (useSpotifyPlayback && currentTrack?.duration_ms) {
      // Spotify seek
      const duration = currentTrack.duration_ms / 1000; // Convert to seconds
      const newPosition = percentage * duration;
      const positionMs = Math.floor(newPosition * 1000); // Convert to ms
      
      console.log(`⏩ Seeking Spotify to ${newPosition.toFixed(2)}s (${positionMs}ms)`);
      
      try {
        await spotifyPlayer.seek(positionMs);
        setCurrentTime(newPosition);
        
        // Broadcast seek to other participants
        send({
          type: 'seek',
          position: newPosition
        });
      } catch (error) {
        console.error('❌ Seek failed:', error);
      }
    } else {
      // HTML5 audio seek
      const duration = audioRef.current?.duration || 0;
      const newPosition = percentage * duration;
      
      send({
        type: 'seek',
        position: newPosition
      });
    }
  };

  // Chat
  const sendChatMessage = () => {
    if (!chatInput.trim()) return;
    
    const messageText = chatInput.trim();
    const newMessage = {
      user_id: userId,
      username: username,
      message: messageText,
      timestamp: new Date().toISOString()
    };
    
    // Optimistic local add so the user always sees their message immediately,
    // even if the WebSocket isn't connected/echoing.
    setChatMessages(prev => [...prev, newMessage]);
    
    // Send via WebSocket
    send({
      type: 'chat_message',
      ...newMessage
    });
    
    setChatInput('');

    // Best-effort: report custom emote usage so AI emote creators see uses tick up
    try {
      const matches = (messageText.match(/:([A-Za-z0-9]{1,8}):/g) || [])
        .map(m => m.slice(1, -1));
      const unique = [...new Set(matches)];
      if (unique.length > 0 && userToken) {
        fetch(`${API}/emote-lab/track-chat-use`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${userToken}` },
          body: JSON.stringify({ names: unique }),
        }).catch(() => { /* fire and forget */ });
      }
    } catch (_e) { /* ignore */ }
  };

  // Reactions
  const sendReaction = (reaction) => {
    // Show reaction locally first
    showReactionAnimation(reaction, username);
    
    // Broadcast to other participants
    send({
      type: 'reaction',
      reaction: reaction,
      username: username
    });
    
    // Keep reactions open so user can keep reacting
    // setShowReactions(false); - removed to keep picker open
  };

  const showReactionAnimation = (reaction, user) => {
    // Create floating reaction element under progress bar
    const reactionEl = document.createElement('div');
    reactionEl.className = 'floating-reaction';
    reactionEl.textContent = reaction;
    reactionEl.style.cssText = `
      position: absolute;
      bottom: 45px;
      left: ${Math.random() * 80 + 10}%;
      font-size: 1.5rem;
      animation: floatUp 2s ease-out forwards;
      z-index: 100;
      pointer-events: none;
    `;
    
    // Try multiple selectors to find a container
    const playerArea = document.querySelector('.jam-player-controls-row') || 
                       document.querySelector('.jam-session-body') ||
                       document.querySelector('.jam-modal-content');
    
    if (playerArea) {
      // Set position if not already set
      const currentPosition = window.getComputedStyle(playerArea).position;
      if (currentPosition === 'static') {
        playerArea.style.position = 'relative';
      }
      
      playerArea.appendChild(reactionEl);
      
      setTimeout(() => {
        if (reactionEl.parentNode) {
          reactionEl.remove();
        }
      }, 2000);
    } else {
      console.error('❌ Could not find container for reaction animation');
    }
  };

  // Leave session
  const handleLeave = async () => {
    try {
      await axios.post(
        `${API}/jam/${session.session_id}/leave`,
        {},
        { headers: { 'Authorization': `Bearer ${userToken}` } }
      );
      disconnect();
      onClose();
    } catch (err) {
      console.error('Failed to leave session:', err);
      onClose();
    }
  };

  // End session (host only)
  const handleEndSession = async () => {
    const confirmed = window.confirm(
      `Are you sure you want to end "${session.name}"? This will remove the session for all participants.`
    );
    
    if (!confirmed) return;
    
    try {
      await axios.delete(
        `${API}/jam/${session.session_id}`,
        { headers: { 'Authorization': `Bearer ${userToken}` } }
      );
      disconnect();
      alert('✅ Session ended successfully');
      onClose();
    } catch (err) {
      console.error('Failed to end session:', err);
      alert(err.response?.data?.detail || 'Failed to end session');
    }
  };

  const formatTime = (time) => {
    if (!time || isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="jam-modal-overlay" onClick={onClose}>
      <div className="jam-modal-with-chat">
        <div className={`jam-modal-content jam-session-modal ${showChatPanel ? 'chat-panel-open' : ''}`} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="jam-session-header" style={{ alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ marginBottom: '0.5rem' }}>🎵 {session.name}</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              {/* WebSocket Status */}
              <span className="jam-session-status" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <span style={{
                  display: 'inline-block',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: isConnected ? '#4CAF50' : '#ff4444',
                  animation: isConnected ? 'blink 2s infinite' : 'none'
                }}></span>
                {isConnected ? 'Connected' : 'Disconnected'}
              </span>
              
              {/* Spotify Status - Clickable */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <button
                  onClick={() => {
                    if (!spotifyPlayer.isReady || !useSpotifyPlayback) {
                      // Navigate to Social Hub
                      alert('💡 To connect Spotify Premium:\n\n1. Go to Social Hub tab\n2. Click "Connect Spotify"\n3. Return to your Jam Session');
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    fontSize: '0.85rem',
                    color: spotifyPlayer.isReady && useSpotifyPlayback ? '#1DB954' : '#ff4444',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '12px',
                    background: spotifyPlayer.isReady && useSpotifyPlayback 
                      ? 'rgba(29, 185, 84, 0.1)' 
                      : 'rgba(255, 68, 68, 0.1)',
                    border: `1px solid ${spotifyPlayer.isReady && useSpotifyPlayback ? 'rgba(29, 185, 84, 0.3)' : 'rgba(255, 68, 68, 0.3)'}`,
                    cursor: spotifyPlayer.isReady && useSpotifyPlayback ? 'default' : 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <span style={{
                    display: 'inline-block',
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: spotifyPlayer.isReady && useSpotifyPlayback ? '#1DB954' : '#ff4444',
                    animation: spotifyPlayer.isReady && useSpotifyPlayback ? 'pulse 2s infinite' : 'none'
                  }}></span>
                  {spotifyPlayer.isReady && useSpotifyPlayback ? 'Spotify Connected' : 'Connect Spotify Premium'}
                </button>
                
                {/* Retry button - Simple green icon, always visible */}
                {spotifyToken && (
                  <span
                    onClick={() => {
                      console.log('🔄 User clicked retry - reconnecting Spotify...');
                      spotifyPlayer.retryConnection();
                    }}
                    style={{
                      cursor: 'pointer',
                      fontSize: '1rem',
                      color: '#1DB954',
                      fontWeight: 'bold',
                      transition: 'transform 0.2s, opacity 0.2s',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0.9,
                      fontFamily: 'monospace'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.transform = 'rotate(180deg) scale(1.2)';
                      e.target.style.opacity = '1';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.transform = 'rotate(0deg) scale(1)';
                      e.target.style.opacity = '0.9';
                    }}
                    title="Reconnect Spotify Player"
                  >
                    ↻
                  </span>
                )}
              </div>
              
              {/* Up Next Display - Show next track in queue with dropdown */}
              {queue.length > 0 && (
                <div style={{ position: 'relative', marginLeft: '1rem' }}>
                  <div 
                    onClick={() => setShowQueueDropdown(!showQueueDropdown)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.25rem 0.75rem',
                      background: 'rgba(0, 212, 255, 0.1)',
                      borderRadius: '8px',
                      border: '1px solid rgba(0, 212, 255, 0.3)',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.background = 'rgba(0, 212, 255, 0.2)'}
                    onMouseOut={(e) => e.currentTarget.style.background = 'rgba(0, 212, 255, 0.1)'}
                  >
                    <span style={{ fontSize: '0.75rem', color: 'rgba(0, 212, 255, 0.9)', fontWeight: '600' }}>
                      ⏭️ Up Next ({queue.length}):
                    </span>
                    {queue[0].album_art && (
                      <img 
                        src={queue[0].album_art} 
                        alt="Next track"
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '3px',
                          objectFit: 'cover'
                        }}
                      />
                    )}
                    <span style={{ 
                      fontSize: '0.75rem', 
                      color: 'rgba(255, 255, 255, 0.9)',
                      maxWidth: '200px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {queue[0].title} - {queue[0].artist}
                    </span>
                    <span style={{ fontSize: '0.65rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                      {showQueueDropdown ? '▲' : '▼'}
                    </span>
                  </div>
                  
                  {/* Queue Dropdown */}
                  {showQueueDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      marginTop: '0.5rem',
                      background: 'rgba(20, 20, 35, 0.98)',
                      border: '1px solid rgba(0, 212, 255, 0.3)',
                      borderRadius: '8px',
                      padding: '0.5rem',
                      minWidth: '300px',
                      maxHeight: '400px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
                    }}>
                      <div style={{ 
                        fontSize: '0.75rem', 
                        color: 'rgba(0, 212, 255, 0.9)', 
                        fontWeight: '600',
                        marginBottom: '0.5rem',
                        paddingBottom: '0.5rem',
                        borderBottom: '1px solid rgba(0, 212, 255, 0.2)'
                      }}>
                        Queue ({queue.length} tracks)
                      </div>
                      {queue.map((track, index) => (
                        <div 
                          key={index}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.5rem',
                            marginBottom: '0.25rem',
                            background: 'rgba(255, 255, 255, 0.03)',
                            borderRadius: '6px',
                            transition: 'background 0.2s',
                            position: 'relative'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.background = 'rgba(0, 212, 255, 0.1)'}
                          onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)'}
                        >
                          <span style={{ 
                            fontSize: '0.7rem', 
                            color: 'rgba(0, 212, 255, 0.7)',
                            minWidth: '20px'
                          }}>
                            {index + 1}.
                          </span>
                          {(track.album_art || track.artwork) ? (
                            <img 
                              src={track.album_art || track.artwork} 
                              alt={track.title}
                              style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '4px',
                                objectFit: 'cover'
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '4px',
                              background: 'rgba(0, 255, 255, 0.1)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '1rem'
                            }}>
                              🎵
                            </div>
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ 
                              fontSize: '0.75rem', 
                              color: 'rgba(255, 255, 255, 0.9)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {track.title}
                            </div>
                            <div style={{ 
                              fontSize: '0.65rem', 
                              color: 'rgba(255, 255, 255, 0.6)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {track.artist}
                            </div>
                          </div>
                          {isHost && (
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button
                                onClick={() => {
                                  console.log('▶️ Play Now clicked for track:', track.title);
                                  // Play this track immediately
                                  setCurrentTrack(track);
                                  setCurrentTime(0);
                                  setIsPlaying(true);
                                  
                                  // Broadcast track change to all participants
                                  send({
                                    type: 'track_changed',
                                    track: track
                                  });
                                  
                                  // Start playback
                                  if (track.spotify_uri && useSpotifyPlayback) {
                                    spotifyPlayer.play(track.spotify_uri, 0);
                                  } else if (audioRef.current) {
                                    audioRef.current.src = track.preview_url;
                                    audioRef.current.currentTime = 0;
                                    audioRef.current.play();
                                  }
                                  
                                  // Close dropdown
                                  setShowQueueDropdown(false);
                                }}
                                style={{
                                  padding: '0.25rem 0.5rem',
                                  fontSize: '0.65rem',
                                  background: 'rgba(29, 185, 84, 0.2)',
                                  border: '1px solid rgba(29, 185, 84, 0.5)',
                                  borderRadius: '4px',
                                  color: '#1DB954',
                                  cursor: 'pointer',
                                  fontWeight: '600',
                                  transition: 'all 0.2s',
                                  whiteSpace: 'nowrap'
                                }}
                                onMouseOver={(e) => {
                                  e.target.style.background = 'rgba(29, 185, 84, 0.3)';
                                  e.stopPropagation();
                                }}
                                onMouseOut={(e) => {
                                  e.target.style.background = 'rgba(29, 185, 84, 0.2)';
                                  e.stopPropagation();
                                }}
                              >
                                ▶️ Play Now
                              </button>
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (window.confirm(`Remove "${track.title}" from queue?`)) {
                                    try {
                                      const axios = (await import('axios')).default;
                                      const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
                                      
                                      console.log('Removing track at index:', index);
                                      console.log('Session ID:', session.session_id);
                                      
                                      await axios.delete(
                                        `${API}/api/jam/${session.session_id}/queue/${index}`,
                                        { headers: { 'Authorization': `Bearer ${userToken}` } }
                                      );
                                      
                                      // Refresh session to update queue
                                      const updatedSession = await axios.get(
                                        `${API}/api/jam/${session.session_id}`,
                                        { headers: { 'Authorization': `Bearer ${userToken}` } }
                                      );
                                      
                                      console.log('Updated queue:', updatedSession.data.queue);
                                      
                                      // Update the queue state
                                      setQueue(updatedSession.data.queue || []);
                                    } catch (error) {
                                      console.error('Error removing track:', error);
                                      console.error('Error details:', error.response?.data);
                                      alert('Failed to remove track. Please try again.');
                                    }
                                  }
                                }}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  fontSize: '1rem',
                                  color: '#ff4444',
                                  cursor: 'pointer',
                                  padding: '0.25rem',
                                  transition: 'all 0.2s',
                                  lineHeight: 1
                                }}
                                onMouseOver={(e) => {
                                  e.target.style.color = '#ff6666';
                                  e.target.style.transform = 'scale(1.2)';
                                  e.stopPropagation();
                                }}
                                onMouseOut={(e) => {
                                  e.target.style.color = '#ff4444';
                                  e.target.style.transform = 'scale(1)';
                                  e.stopPropagation();
                                }}
                                title="Remove from queue"
                              >
                                ✕
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Action Buttons - Smaller and aligned on same line */}
              <div style={{ display: 'flex', gap: '0.4rem', marginLeft: 'auto' }}>
                {isHost && (
                  <>
                    <button 
                      className="jam-btn jam-btn-primary" 
                      onClick={() => setShowInviteFriends(true)}
                      style={{
                        padding: '0.35rem 0.7rem',
                        fontSize: '0.75rem'
                      }}
                    >
                      📨 Invite
                    </button>
                    <button 
                      className="jam-btn jam-btn-secondary" 
                      onClick={handleLeave}
                      style={{
                        padding: '0.35rem 0.7rem',
                        fontSize: '0.75rem'
                      }}
                    >
                      👋 Leave
                    </button>
                    <button 
                      className="jam-btn jam-btn-danger" 
                      onClick={handleEndSession}
                      style={{
                        padding: '0.35rem 0.7rem',
                        fontSize: '0.75rem'
                      }}
                    >
                      🗑️ End Session
                    </button>
                  </>
                )}
                {!isHost && (
                  <button 
                    className="jam-btn jam-btn-secondary" 
                    onClick={handleLeave}
                    style={{
                      padding: '0.35rem 0.7rem',
                      fontSize: '0.75rem'
                    }}
                  >
                    👋 Leave
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Spotify Premium Alert */}
        {showPremiumAlert && spotifyToken && !spotifyPlayer.isPremium && (
          <div style={{
            background: 'linear-gradient(135deg, #1DB954 0%, #1ed760 100%)',
            padding: '1rem',
            borderRadius: '8px',
            margin: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            border: '2px solid rgba(29, 185, 84, 0.3)'
          }}>
            <span style={{ fontSize: '2rem' }}>🎵</span>
            <div style={{ flex: 1 }}>
              <h4 style={{ margin: '0 0 0.5rem 0', color: '#fff' }}>Spotify Premium Required</h4>
              <p style={{ margin: 0, fontSize: '0.9rem', color: 'rgba(255,255,255,0.9)' }}>
                Full track playback requires Spotify Premium. You can still enjoy 30-second previews!
              </p>
            </div>
            <button 
              onClick={() => setShowPremiumAlert(false)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '1.5rem',
                padding: '0.5rem'
              }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Spotify Player Status Banner */}
        {!useSpotifyPlayback && currentTrack?.spotify_uri && (
          <div style={{
            background: 'linear-gradient(135deg, rgba(29, 185, 84, 0.1) 0%, rgba(30, 215, 96, 0.1) 100%)',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            margin: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            border: '1px solid rgba(29, 185, 84, 0.3)'
          }}>
            <span style={{ fontSize: '1.2rem' }}>🎵</span>
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontSize: '0.85rem', color: 'rgba(255,255,255,0.8)' }}>
                {spotifyToken 
                  ? `Spotify Player initializing... (Player Ready: ${spotifyPlayer.isReady ? 'Yes' : 'Waiting...'} | Device ID: ${spotifyPlayer.deviceId ? 'Received' : 'Waiting...'})`
                  : '💡 Connect Spotify in Social Hub for full track playback'}
              </p>
            </div>
            {spotifyToken && !spotifyPlayer.isReady && (
              <button
                onClick={() => {
                  console.log('🔄 User clicked retry button');
                  spotifyPlayer.retryConnection();
                }}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  background: 'rgba(29, 185, 84, 0.2)',
                  border: '1px solid rgba(29, 185, 84, 0.5)',
                  color: '#1DB954',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.target.style.background = 'rgba(29, 185, 84, 0.3)'}
                onMouseOut={(e) => e.target.style.background = 'rgba(29, 185, 84, 0.2)'}
              >
                🔄 Retry
              </button>
            )}
          </div>
        )}

        {/* Spotify Connected status is now shown in header only - removed redundant banner */}

        {/* Main Content */}
        <div className="jam-session-body">
          {/* Player Section */}
          <div className="jam-player-section">
            {currentTrack ? (
              <>
                {currentTrack.artwork && (
                  <img 
                    src={currentTrack.artwork} 
                    alt={currentTrack.title}
                    className="jam-album-art"
                  />
                )}
                <h3 className="jam-track-title">{currentTrack.title}</h3>
                <p className="jam-track-artist">{currentTrack.artist}</p>

                {/* No Audio Source Warning */}
                {!currentTrack.preview_url && !currentTrack.spotify_uri && (
                  <div style={{
                    background: 'rgba(255, 165, 0, 0.15)',
                    border: '1px solid rgba(255, 165, 0, 0.5)',
                    borderRadius: '8px',
                    padding: '0.75rem',
                    margin: '1rem 0',
                    color: '#ffa500',
                    fontSize: '0.85rem',
                    textAlign: 'center'
                  }}>
                    ⚠️ This track has no playable audio source. Try adding a track from Spotify search.
                  </div>
                )}

                {/* Audio Player */}
                <audio 
                  ref={audioRef} 
                  src={currentTrack.preview_url}
                  preload="auto"
                  onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
                  onLoadedMetadata={(e) => {
                    console.log('🎵 Audio loaded successfully');
                    console.log('   Duration:', e.target.duration);
                    console.log('   URL:', currentTrack.preview_url);
                  }}
                  onError={(e) => {
                    console.error('❌ Audio error details:');
                    console.error('   Error code:', e.target.error?.code);
                    console.error('   Error message:', e.target.error?.message);
                    console.error('   URL attempted:', currentTrack.preview_url);
                    
                    // Error code 2 is MEDIA_ERR_NETWORK (network/stream error)
                    if (e.target.error?.code === 2) {
                      console.log('🔄 Network error detected - attempting to recover...');
                      
                      // Try to reload the audio
                      const currentTime = audioRef.current?.currentTime || 0;
                      if (currentTime < 2) {
                        // If error happens early, reload from start
                        setTimeout(() => {
                          console.log('🔄 Reloading audio from start...');
                          if (audioRef.current) {
                            audioRef.current.load();
                            if (isPlaying) {
                              audioRef.current.play().catch(err => console.error('Retry play error:', err));
                            }
                          }
                        }, 500);
                      } else {
                        // If playing fine then fails, show error
                        alert('Audio stream interrupted. This track preview may not be fully available.');
                        setIsPlaying(false);
                      }
                    } else if (e.target.error?.code === 4) {
                      alert('This track format is not supported or the preview URL is invalid.');
                      setIsPlaying(false);
                    } else {
                      alert('Failed to load audio: ' + (e.target.error?.message || 'Unknown error'));
                      setIsPlaying(false);
                    }
                  }}
                  onEnded={() => {
                    console.log('🎵 Track ended naturally');
                    setIsPlaying(false);
                  }}
                  onPause={() => {
                    console.log('⏸ Audio paused');
                  }}
                  onPlay={() => {
                    console.log('▶ Audio playing successfully');
                  }}
                  onStalled={() => {
                    console.warn('⚠️ Audio stalled/buffering - waiting...');
                  }}
                  onCanPlay={() => {
                    console.log('✅ Audio can play - ready');
                  }}
                  onWaiting={() => {
                    console.log('⏳ Waiting for data...');
                  }}
                />

                {/* Player Controls Row */}
                <div className="jam-player-controls-row">
                  {/* Play Button */}
                  <button
                    className="jam-play-btn"
                    onClick={togglePlayPause}
                    disabled={!isHost}
                    title={isHost ? (
                      useSpotifyPlayback ? 
                        (spotifyPlayer.isPlaying ? 'Pause' : 'Play') :
                        (isPlaying ? 'Pause' : 'Play')
                    ) : 'Only host can control playback'}
                  >
                    {useSpotifyPlayback ? (spotifyPlayer.isPlaying ? '⏸' : '▶') : (isPlaying ? '⏸' : '▶')}
                  </button>

                  {/* Progress Bar */}
                  <div 
                    className="jam-progress-bar"
                    onClick={isHost ? handleSeek : null}
                    style={{ cursor: isHost ? 'pointer' : 'default' }}
                  >
                    <div 
                      className="jam-progress-fill"
                      style={{ 
                        width: `${
                          useSpotifyPlayback && currentTrack?.duration_ms
                            ? (currentTime / (currentTrack.duration_ms / 1000)) * 100
                            : audioRef.current?.duration 
                            ? (currentTime / audioRef.current.duration) * 100 
                            : 0
                        }%` 
                      }}
                    />
                  </div>

                  {/* Time Display and React Button - Right Side */}
                  <div className="jam-time-display-wrapper">
                    <span className="jam-time-display">
                      {formatTime(currentTime)} / {formatTime(
                        useSpotifyPlayback && currentTrack?.duration_ms
                          ? currentTrack.duration_ms / 1000
                          : audioRef.current?.duration || 0
                      )}
                    </span>
                    
                    {/* React Button - Below time display on right */}
                    <div className="jam-reactions-below-time-right" style={{ position: 'relative' }}>
                      <button 
                        className="jam-btn jam-btn-secondary"
                        style={{
                          padding: '0.25rem 0.5rem',
                          fontSize: '0.7rem'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowReactions(!showReactions);
                        }}
                      >
                        😊 React
                      </button>
                      {showReactions && (
                        <>
                          {/* Invisible backdrop to catch clicks outside */}
                          <div 
                            style={{
                              position: 'fixed',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              zIndex: 99
                            }}
                            onClick={() => setShowReactions(false)}
                          />
                          <div 
                            className="jam-reaction-picker"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {['🔥', '❤️', '💯', '🎵', '👏', '🎉'].map(emoji => (
                              <button
                                key={emoji}
                                className="jam-reaction-btn"
                                onClick={() => sendReaction(emoji)}
                              >
                                {emoji}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="jam-no-track">
                {/* Add Track to Begin Button (Host Only) - Center */}
                {isHost && (
                  <button 
                    className="jam-btn jam-btn-primary"
                    onClick={() => setShowAddTrackModal(true)}
                  >
                    + Add Track to Begin
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Participants */}
          <div className="jam-participants-section">
            <h4>👥 Listening Now ({participants.length})</h4>
            <div className="jam-participants-list">
              {participants.map(p => (
                <div key={p.user_id} className="jam-participant">
                  <span className="jam-participant-name">
                    {p.username} {p.is_host && '👑'}
                  </span>
                </div>
              ))}
            </div>
            
            {/* Add Track to Queue Button (Host Only) - Max 5 tracks */}
            {isHost && (
              <>
                <button 
                  className="jam-btn jam-btn-primary jam-btn-small jam-add-track-below-participants"
                  onClick={() => {
                    if (queue.length >= 5) {
                      alert('Queue is full! Maximum 5 tracks allowed.');
                    } else {
                      setShowAddTrackModal(true);
                    }
                  }}
                  disabled={queue.length >= 5}
                  title={queue.length >= 5 ? 'Queue full (5 tracks max)' : `Add to queue (${queue.length}/5)`}
                >
                  + Add Track to Queue {queue.length > 0 && `(${queue.length}/5)`}
                </button>
                
                {/* Chat Box Button - Below Add Track */}
                <button 
                  className="jam-btn jam-btn-secondary jam-btn-small jam-chat-box-below-queue"
                  onClick={() => setShowChatPanel(!showChatPanel)}
                >
                  {showChatPanel ? '✕ Close' : '💬 Chat Box'}
                </button>
              </>
            )}
          </div>

          {/* Chat Input - Always visible at bottom */}
          <div className="jam-chat-input-section">
            <div className="jam-chat-input-header">
              <h4>💬 Chat</h4>
            </div>
            <div className="jam-chat-input-container">
              <button 
                className="jam-emote-btn"
                onClick={() => setShowEmotePicker(true)}
                title="Add emote"
              >
                😊
              </button>
              <EmoteInput
                value={chatInput}
                onChange={setChatInput}
                onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                placeholder="Type a message..."
                globalEmotes={globalEmotes}
                customEmotes={customEmotes}
                className="jam-chat-input"
              />
              <button 
                className="jam-btn jam-btn-primary jam-btn-small"
                onClick={sendChatMessage}
              >
                Send
              </button>
            </div>
          </div>
        </div>
        </div>

        {/* Mobile chat backdrop: dims the jam modal when chat is open */}
        {showChatPanel && (
          <div
            className="jam-chat-mobile-backdrop"
            onClick={() => setShowChatPanel(false)}
            aria-hidden="true"
          />
        )}

        {/* Sliding Chat Panel - Outside modal */}
        <div className={`jam-chat-slide-panel ${showChatPanel ? 'open' : ''}`} onClick={(e) => e.stopPropagation()}>
          <div className="jam-chat-panel-content">
            <button
              className="jam-chat-mobile-close"
              onClick={() => setShowChatPanel(false)}
              aria-label="Close chat"
              title="Close chat"
            >
              ✕
            </button>
            <h4>💬 Chat Box</h4>
            <div className="jam-chat-messages-scroll">
              {chatMessages.length === 0 ? (
                <p className="jam-chat-empty">No messages yet. Start the conversation!</p>
              ) : (
                chatMessages.map((msg, idx) => (
                  <div key={idx} className="jam-chat-message">
                    <span className="jam-chat-username">{msg.username}:</span>
                    <span 
                      className="jam-chat-text"
                      dangerouslySetInnerHTML={{ __html: renderEmotesInText(msg.message, globalEmotes, customEmotes) }}
                    />
                  </div>
                ))
              )}
              <div ref={chatEndRef} />
            </div>
          </div>
        </div>
      </div>

      {/* Add Track Modal */}
      {showAddTrackModal && (
        <AddTrackModal
          isOpen={showAddTrackModal}
          onClose={() => setShowAddTrackModal(false)}
          onSelectFavorites={() => {
            setShowAddTrackModal(false);
            setTrackSelectionSource('favorites');
            setShowTrackSelection(true);
          }}
          onSelectSaved={() => {
            setShowAddTrackModal(false);
            setTrackSelectionSource('saved');
            setShowTrackSelection(true);
          }}
          onDiscoverNew={() => {
            setShowAddTrackModal(false);
            setShowDiscoverAlert(true);
          }}
          onSelectSpotify={() => {
            setShowAddTrackModal(false);
            setShowSpotifyLibrary(true);
          }}
          onSelectLocal={onSelectLocalTrack ? () => {
            setShowAddTrackModal(false);
            console.log('🎵 JamSessionModal calling onSelectLocalTrack with session:', session.session_id);
            onSelectLocalTrack(session.session_id);
          } : null}
        />
      )}

      {/* Track Selection Modal */}
      {showTrackSelection && (
        <TrackSelectionModal
          isOpen={showTrackSelection}
          onClose={() => {
            setShowTrackSelection(false);
            setTrackSelectionSource(null);
          }}
          onBack={() => {
            setShowTrackSelection(false);
            setTrackSelectionSource(null);
            setShowAddTrackModal(true);
          }}
          onSelectTrack={(track) => {
            console.log('✅ Track added to queue:', track);
            // Refresh session to update queue
            refreshSession();
          }}
          sessionId={session.session_id}
          userToken={userToken}
          sourceType={trackSelectionSource}
        />
      )}

      {/* Invite Friends Modal */}
      {showInviteFriends && (
        <InviteFriendsModal
          isOpen={showInviteFriends}
          onClose={() => setShowInviteFriends(false)}
          sessionId={session.session_id}
          sessionName={session.name}
          userToken={userToken}
        />
      )}

      {/* Discover New Alert */}
      {showDiscoverAlert && (
        <DiscoverNewAlert
          isOpen={showDiscoverAlert}
          onClose={() => setShowDiscoverAlert(false)}
          onBack={() => {
            setShowDiscoverAlert(false);
            setShowAddTrackModal(true);
          }}
          onProceed={() => {
            setShowDiscoverAlert(false);
            // Set discovery mode flags
            if (setIsDiscoveringForJam && setTargetSessionId) {
              setIsDiscoveringForJam(true);
              setTargetSessionId(session.session_id);
            }
            // Navigate to Discovery Zone
            if (onNavigateToDiscover) {
              onNavigateToDiscover();
            }
            // Close jam session modal
            onClose();
          }}
        />
      )}


      {/* Spotify Library Modal */}
      {showSpotifyLibrary && (
        <SpotifyLibraryModal
          isOpen={showSpotifyLibrary}
          onClose={() => setShowSpotifyLibrary(false)}
          onBack={() => {
            setShowSpotifyLibrary(false);
            setShowAddTrackModal(true);
          }}
          onSelectTrack={(track) => {
            setShowSpotifyLibrary(false);
            // Track is already added to queue via API call in SpotifyLibraryModal
            // Just update local state
            setQueue(prev => [...prev, track]);
            if (!currentTrack) {
              setCurrentTrack(track);
            }
          }}
          sessionId={session.session_id}
          userToken={userToken}
        />
      )}


      {/* Emote Picker */}
      <EmotePicker 
        isOpen={showEmotePicker}
        onClose={() => setShowEmotePicker(false)}
        onEmoteSelect={(emoteCode) => {
          console.log('Emote selected:', emoteCode);
          setChatInput(prev => prev + emoteCode);
          setShowEmotePicker(false);
        }}
      />
    </div>
  );
};

export default JamSessionModal;
